import React, { createContext, useContext, useEffect, useState } from 'react';
import { node } from 'prop-types';
import AUISpinner from '@ancert/aui-spinner';
import { useNotification } from '@ancert/aui-notification-context';
import lightenDarkenColor from '../utils/colorUtils';
import { DARK_AMOUNT, LIGHT_AMOUNT } from '../constants';
import { getInstance } from '../services/config.service';
import CONFIG_KEY from '../constants/config.constants';

const contextDefaultValue = {
  homeText: null,
  legalText: '',
  mainImage: '',
  mainImageType: '',
  loadTheme: null,
};

const ThemeContext = createContext(contextDefaultValue);

/**
 * Componente que genera el Layout
 *
 * @component
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
const ThemeProvider = ({ children }) => {
  const [homeText, setHomeText] = useState(null);
  const [loadingTheme, setLoadingTheme] = useState(false);
  const [mainImage, setMainImage] = useState('');
  const [mainImageType, setMainImageType] = useState('');
  const [legalText, setLegalText] = useState('');
  const { openNotification } = useNotification();

  const setButtonColor = ({ color, mode }) => {
    const { style } = document.documentElement;
    style.setProperty(`--p-button-${mode}--bgColor`, color);
    style.setProperty(`--p-button-${mode}--hocus--bgColor`, `${lightenDarkenColor(color, LIGHT_AMOUNT)}`);
    style.setProperty(`--p-button-${mode}--active--bgColor`, `${lightenDarkenColor(color, DARK_AMOUNT)}`);
  };

  const setStepColor = ({ color }) => {
    const { style } = document.documentElement;
    style.setProperty('--aui-steps__tit--active--bgColor', color);
  };

  const setTabColor = ({ color }) => {
    const { style } = document.documentElement;
    style.setProperty('--aui-tab__btn--active--after--bgColor', color);
  };

  /**
   *
   * @param {string} [message]
   * @returns
   */
  const errorModal = (message) =>
    openNotification({
      title: 'Error',
      content: <div>{message || 'Ha ocurrido un error recuperando los datos'}</div>,
      type: 'error',
      centerButtons: true,
    });

  /**
   *
   * @param {string} texto
   */
  const loadTexto = (texto) => {
    try {
      const textObj = JSON.parse(texto);
      setHomeText(textObj);
    } catch (e) {
      console.error(e);
      errorModal();
    }
  };

  /**
   *
   * @param {string} tema
   */
  const loadTema = (tema) => {
    try {
      const { primaryColor, secondaryColor, mainImage: image, mainImageType: imageType } = JSON.parse(tema);
      if (primaryColor) {
        setButtonColor({ color: primaryColor, mode: 'primary' });
        setStepColor({ color: primaryColor });
        setTabColor({ color: primaryColor });
      }
      if (secondaryColor) {
        setButtonColor({ color: secondaryColor, mode: 'secondary' });
      }

      setMainImage(image);
      setMainImageType(imageType);
    } catch (e) {
      console.error(e);
      errorModal();
    }
  };

  const loadTheme = async (isChange = false) => {
    setLoadingTheme(true);
    const { status, data: result, error } = await getInstance();
    if (status === 200) {
      const tema = result.find(({ key }) => key === CONFIG_KEY.TEMA);
      const texto = result.find(({ key }) => key === CONFIG_KEY.TEXTO);
      const legal = result.find(({ key }) => key === CONFIG_KEY.LEGAL);

      loadTema(tema?.value);
      loadTexto(texto?.value);
      setLegalText(legal?.value || '');

      setLoadingTheme(false);
      if (isChange) {
        openNotification({
          title: 'Success',
          content: <div>El cambio de la configuración ha sido exitoso</div>,
          type: 'success',
          centerButtons: true,
        });
      }

      return;
    }
    const { message } = error;
    errorModal(message || error);
    setLoadingTheme(false);
  };

  useEffect(() => {
    loadTheme();
  }, []);

  const value = {
    homeText,
    legalText,
    mainImage,
    mainImageType,
    loadTheme,
  };
  return (
    <ThemeContext.Provider value={value}>
      {loadingTheme ? (
        <div className="c-sii-loading">
          <AUISpinner size="lg" />
          Cargando
        </div>
      ) : (
        children
      )}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: node.isRequired,
};

const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within provider');
  }

  return context;
};

export { ThemeProvider, ThemeContext, useTheme };
